import { Injectable, Injector } from '@angular/core';
import { ApplicationService } from '@app/shared/application/application.service';
import { Observable, of, ReplaySubject } from 'rxjs';
import { delay, map, tap } from 'rxjs/operators';
import { SelectItem } from '@node_modules/primeng/api';
import { AppComponentBase } from '@shared/common/app-component-base';
import { RlaDto, GeneralFileInfoDto, PersonApplicationSummaryDto, LiaTypeServiceProxy, RlaApplicantPersonServiceProxy, RlaApplicantContactServiceProxy, RlaPersonServiceProxy, RlaBusinessServiceProxy, RlaInvoiceAddressServiceProxy, RlaDocumentServiceProxy, ExternalSystemEnum, CreateRlaInput, CreateRlaOutput, GetRlaOutput, RlaStatus, ProfessionalLicenceDto, GeneralFileDto, GetRlaFileInput, ApplicationUploadFilesInput, ApplicationStatus, RlaApplicantPersonDto, GeneralDocumentGroupDto, GetRlaDocumentsInput, RlaDocumentsOutput, RlaApplicantContactDto, CreateOrUpdateRlaApplicantContact, RlaPersonDto, CreateOrUpdateRlaPerson, RlaBusinessDto, GetRlaInvoiceAddressOutput, RlaInvoiceAddressDto, InvoiceAddressDetailDto, RlaServiceProxy, RlaReplacementServiceProxy, RlaReplacementDto, RlaAcademicTitleServiceProxy, CreateOrUpdateRlaAcademicTitleInput, CreateOrUpdateRlaAcademicTitleOutput, RlaAcademicTitleDto, CreateOrUpdateRlaMedicalFieldDirector, RlaMedicalFieldDirectorServiceProxy, RlaMedicalFieldDirectorDto, RlaRestrictionsServiceProxy, CreateOrUpdateRlaRestrictionsInput, RlaRestrictionDto, RlaRestrictionGeneralInformationDto, RlaEmploymentDto, RlaEmploymentServiceProxy } from '@shared/service-proxies/service-proxies';
import { AppConsts } from '@shared/AppConsts';

export interface RlaRestrictionOutput {
    restrictions: RlaRestrictionDto[],
    generalInformation: RlaRestrictionGeneralInformationDto
}

@Injectable({providedIn: 'root'})
export class RlaService extends AppComponentBase {

    rla: RlaDto;
    rlaResultDocuments: GeneralFileInfoDto[];

    private rlapsForPersonSubject = new ReplaySubject<PersonApplicationSummaryDto[]>(1);
    readonly rlapsForPerson$ = this.rlapsForPersonSubject.asObservable();

    constructor(
        injector: Injector,
        private applicationService: ApplicationService,
        private liaService: LiaTypeServiceProxy,
        private rlaService: RlaServiceProxy,
        private rlaApplicantPersonServiceProxy: RlaApplicantPersonServiceProxy,
        private rlaApplicantContactServiceProxy: RlaApplicantContactServiceProxy,
        private rlaPersonServiceProxy: RlaPersonServiceProxy,
        private rlaBusinessServiceProxy: RlaBusinessServiceProxy,
        private replacementService: RlaReplacementServiceProxy,
        private invoiceAddressService: RlaInvoiceAddressServiceProxy,
        private documentService: RlaDocumentServiceProxy,
        private rlaAcademicTitleService: RlaAcademicTitleServiceProxy,
        private rlaMedicalFieldDirectorService: RlaMedicalFieldDirectorServiceProxy,
        private rlaRestrictionsService: RlaRestrictionsServiceProxy,
        private rlaEmploymentService: RlaEmploymentServiceProxy
    ) {
        super(injector);
    }

    loadRlasForPerson(gln: string): Observable<PersonApplicationSummaryDto[]> {
        if (gln && this.isGranted('Pages.Authority.Applications.Person')) {
            return this.rlaService.getRlasForPerson(gln).pipe(
                tap(rlaps => this.rlapsForPersonSubject.next(rlaps))
            );
        } else {
            this.rlapsForPersonSubject.next([]);
            return of([]);
        }
    }

    getExternalSystemByProfessionId(professionId: string): Observable<ExternalSystemEnum> {
        return this.liaService.getExternalRegisterByProfessionId(professionId);
    }

    createRla(input: CreateRlaInput): Observable<CreateRlaOutput> {
        return this.rlaService.createRla(input).pipe(tap((output: CreateRlaOutput) => {
            this.rla = null;
        }));
    }

    deleteRla(): Observable<void> {
        return this.applicationService.deleteApplication();
    }

    getRla(caseId: string): Observable<GetRlaOutput> {
        return this.rlaService.getRla(caseId).pipe(tap((output: GetRlaOutput) => {
            this.rla = output.rla;
        }));
    }

    closeRla(): void {
        this.rla = null;
    }

    getMyRlas(): Observable<PersonApplicationSummaryDto[]> {
        return this.rlaService.getMyRlas();
    }

    isInRlaStatus(expectedStatus: RlaStatus): boolean {
        return this.rla.status === expectedStatus;
    }

    getPersonalLicenceForRla(): Observable<ProfessionalLicenceDto> {
        return this.rlaService.getLicenceForRla(this.caseId);
    }


    getEvaluationComments(): Observable<string> {
        return this.rlaService.getEvaluationComments(this.caseId);
    }

    releaseRla(comment: string): Observable<void> {
        return this.rlaService.releaseRla(this.applicationService.caseId, comment);
    }

    getNextStatus(status: RlaStatus): Observable<RlaStatus> {
        let isAuthority = this.permission.isGranted('Pages.Authority.Applications');
        return this.rlaService.getNextStatus(this.caseId, status).pipe(map(status => {
            // Authority goes to 'finish review' and not to 'release'
            if (isAuthority && status === RlaStatus.Release) {
                return RlaStatus.FinishReview;
            }
            return status;
        }));
    }

    getStepUrl(nextStep: RlaStatus): string {
        return this.applicationService.getNextStep(RlaService.getUrlForStep(nextStep));
    }

    getFile(step: RlaStatus, id: number): Observable<GeneralFileDto> {
        const input = new GetRlaFileInput();
        input.caseId = this.caseId;
        input.step = step;
        input.id = id;
        return this.documentService.getFile(input);
    }

    uploadFiles(input: ApplicationUploadFilesInput): Observable<GeneralFileInfoDto[]> {
        return this.documentService.uploadFiles(input);
    }

    deleteFile(step: RlaStatus, id: number): Observable<void> {
        return this.documentService.deleteFile(this.caseId, step, id);
    }

    get caseId(): string {
        return this.applicationService.caseId;
    }

    get status(): ApplicationStatus {
        return this.applicationService.status;
    }

    get professionId(): string {
        return this.rla?.professionId;
    }

    getApplicantName(): Observable<string> {
        return of(this.rla.application.name);
    }

    setApplicantPersonStepToReview() {
        this.applicationService.setStepToReview(RlaStatus.ApplicantPerson);
    }

    getApplicantPersonFiles(forceRefresh?: boolean, caseId = this.caseId): Observable<GeneralDocumentGroupDto[]> {
        return this.documentService.getDocuments(new GetRlaDocumentsInput({ caseId: caseId, step: RlaStatus.ApplicantPerson, hasImmigratedLastFiveYearsId: null, hasLicenceInThisCanton: null})).pipe(
            map((output: RlaDocumentsOutput) => output.documents)
        );
    }

    getApplicantPerson(): Observable<RlaApplicantPersonDto> {
        return this.rlaApplicantPersonServiceProxy.get(this.caseId);
    }

    performRegisterCheck(gln: string, caseId = this.caseId): Observable<RlaApplicantPersonDto> {
        this.applicationService.setStepToReview(RlaStatus.ApplicantPerson);
        return this.rlaApplicantPersonServiceProxy.performRegisterCheck(caseId, gln);
    }

    acceptRegisterCheck(): Observable<RlaStatus> {
        this.applicationService.setStepToReview(RlaStatus.ApplicantPerson);
        return this.rlaApplicantPersonServiceProxy.acceptRegisterCheck(this.caseId).pipe(
            tap((status: RlaStatus) => {
                this.rla.currentStep = status;
            })
        );
    }

    getApplicantContact(): Observable<RlaApplicantContactDto> {
        return this.rlaApplicantContactServiceProxy.get(this.caseId);
    }

    createOrUpdateApplicantContact(input: CreateOrUpdateRlaApplicantContact): Observable<void> {
        this.applicationService.setStepToReview(RlaStatus.ApplicantContact);
        return this.rlaApplicantContactServiceProxy.createOrUpdate(input).pipe(
            tap((status: RlaStatus) => {
                this.rla.currentStep = status;
            }),
            map((status: RlaStatus) => null)
        );
    }

    getPersonFiles(forceRefresh?: boolean, hasLicenceInThisCanton?: boolean, caseId = this.caseId): Observable<GeneralDocumentGroupDto[]> {
        return this.documentService.getDocuments(new GetRlaDocumentsInput({ caseId: caseId, step: RlaStatus.Person, hasImmigratedLastFiveYearsId: null, hasLicenceInThisCanton: hasLicenceInThisCanton})).pipe(
            map((output: RlaDocumentsOutput) => output.documents)
        );
    }

    getPerson(): Observable<RlaPersonDto> {
        return this.rlaPersonServiceProxy.get(this.caseId);
    }

    createOrUpdatePerson(input: CreateOrUpdateRlaPerson): Observable<void> {
        this.applicationService.setStepToReview(RlaStatus.Person);
        return this.rlaPersonServiceProxy.createOrUpdate(input).pipe(
            tap((status: RlaStatus) => {
                this.rla.currentStep = status;
            }),
            map((status: RlaStatus) => null)
        );
    }


    getBusinesses(): Observable<RlaBusinessDto[]> {
        return this.rlaBusinessServiceProxy.getList(this.caseId);
    }

    createOrUpdateBusiness(caseId: string, input: RlaBusinessDto[]): Observable<void> {
        this.applicationService.setStepToReview(RlaStatus.Business);
        return this.rlaBusinessServiceProxy.createOrUpdate(caseId, input).pipe(
            tap((status: RlaStatus) => {
                this.rla.currentStep = status;
            }),
            map((status: RlaStatus) => null)
        );
    }

    getReplacement(): Observable<RlaReplacementDto> {
        return this.replacementService.get(this.caseId);
    }

    createOrUpdateReplacement(replacement: RlaReplacementDto): Observable<any> {
        this.applicationService.setStepToReview(RlaStatus.Replacement);
        return this.replacementService.createOrUpdate(this.caseId, replacement).pipe(tap((newStatusId: RlaStatus) => {
            this.rla.status = newStatusId;
        }));
    }

    getInvoiceAddress(): Observable<GetRlaInvoiceAddressOutput> {
        return this.invoiceAddressService.get(this.caseId);
    }

    createOrUpdateInvoiceAddress(invoiceAddress: RlaInvoiceAddressDto): Observable<any> {
        this.applicationService.setStepToReview(RlaStatus.InvoiceAddress);
        return this.invoiceAddressService.createOrUpdate(this.caseId, invoiceAddress).pipe(tap((newStatusId: RlaStatus) => {
            this.rla.status = newStatusId;
        }));
    }

    getAcademicTitles(): Observable<RlaAcademicTitleDto[]> {
        return of(this.rla.rlaAcademicTitles.titles);
    }

    createBlankRlaAcademicTitle(): Observable<string> {
        return this.rlaAcademicTitleService.createBlankRlaAcademicTitle(this.caseId);
    }

    deleteRlaAcademicTitle(guid: string): Observable<void> {
        return this.rlaAcademicTitleService.deleteRlaAcademicTitle(this.caseId, guid);
    }

    createOrUpdateRlaAcademicTitles(input: CreateOrUpdateRlaAcademicTitleInput): Observable<void> {
        this.applicationService.setStepToReview(RlaStatus.AcademicTitle);
        return this.rlaAcademicTitleService.createOrUpdateRlaAcademicTitle(input).pipe(tap((output: CreateOrUpdateRlaAcademicTitleOutput) => {
            this.rla.rlaAcademicTitles.titles = output.rlaAcademicTitles;
            this.rla.currentStep = output.status;
        })).pipe(map(() => null));
    }

    getRlaAcademicTitleFiles(forceRefresh?: boolean, caseId = this.applicationService.caseId): Observable<GeneralDocumentGroupDto[]> {
        if (forceRefresh) {
            return this.rlaAcademicTitleService.getRlaAcademicTitleFiles(caseId, RlaStatus.AcademicTitle, undefined, undefined).pipe(tap((output: RlaDocumentsOutput) => {
                this.rla.rlaAcademicTitles.documents = output.documents;
            })).pipe(map((output: RlaDocumentsOutput) => output.documents));
        }
        return of(this.rla.rlaAcademicTitles.documents);
    }

    uploadRlaAcademicTitleFiles(rlaAcademicTitleId: string, input: ApplicationUploadFilesInput): Observable<GeneralFileInfoDto[]> {
        return this.rlaAcademicTitleService.uploadFiles(rlaAcademicTitleId, input);
    }

    getMedicalFieldDirectorFiles(forceRefresh?: boolean, caseId = this.caseId): Observable<GeneralDocumentGroupDto[]> {
        return this.documentService.getDocuments(new GetRlaDocumentsInput({ caseId: caseId, step: RlaStatus.MedicalFieldDirector, hasImmigratedLastFiveYearsId: null, hasLicenceInThisCanton: null})).pipe(
            map((output: RlaDocumentsOutput) => output.documents)
        );
    }

    getMedicalFieldDirector(): Observable<RlaMedicalFieldDirectorDto> {
        return this.rlaMedicalFieldDirectorService.get(this.caseId);
    }

    createOrUpdateMedicalFieldDirector(input: CreateOrUpdateRlaMedicalFieldDirector): Observable<void> {
        this.applicationService.setStepToReview(RlaStatus.MedicalFieldDirector);
        return this.rlaMedicalFieldDirectorService.createOrUpdate(input).pipe(
            tap((status: RlaStatus) => {
                this.rla.currentStep = status;
            }),
            map((status: RlaStatus) => null)
        );
    }

    getRestrictionFiles(forceRefresh?: boolean, hasLicenceInThisCanton?: boolean, caseId = this.caseId): Observable<GeneralDocumentGroupDto[]> {
        return this.documentService.getDocuments(new GetRlaDocumentsInput({ caseId: caseId, step: RlaStatus.Restriction, hasImmigratedLastFiveYearsId: null, hasLicenceInThisCanton: hasLicenceInThisCanton})).pipe(
            map((output: RlaDocumentsOutput) => output.documents)
        );
    }

    getRestrictions(): Observable<RlaRestrictionOutput> {
        return of({
            restrictions: this.rla.rlaRestrictions,
            generalInformation: this.rla.rlaRestrictionGeneralInformationDto
        }).pipe(delay(1));
    }

    createOrUpdateRlaRestrictions(input: CreateOrUpdateRlaRestrictionsInput): Observable<void> {
        this.applicationService.setStepToReview(RlaStatus.Restriction);
        return this.rlaRestrictionsService.createOrUpdateRlaRestrictions(input).pipe(tap((status: RlaStatus) => {
            this.rla.rlaRestrictions = input.rlaRestrictions;
            this.rla.rlaRestrictionGeneralInformationDto = input.rlaRestrictionGeneralInformation;
            this.rla.currentStep = status;
        }))
        .pipe(map((status: RlaStatus) => null));
    }

    getEmployment(): Observable<RlaEmploymentDto> {
        return of(this.rla.rlaEmployment).pipe(delay(1));
    }

    createOrUpdateEmployment(input: RlaEmploymentDto): Observable<void> {
        this.applicationService.setStepToReview(RlaStatus.Employment);
        return this.rlaEmploymentService.createOrUpdateRlaEmployment(this.caseId, input).pipe(
            tap((status: RlaStatus) => {
                this.rla.currentStep = status;
                this.rla.rlaEmployment = input;
            }),
            map((status: RlaStatus) => null)
        );
    }

    getEmploymentFiles(forceRefresh?: boolean, caseId = this.caseId): Observable<GeneralDocumentGroupDto[]> {
        return this.documentService.getDocuments(new GetRlaDocumentsInput({ caseId: caseId, step: RlaStatus.Employment, hasImmigratedLastFiveYearsId: null, hasLicenceInThisCanton: null})).pipe(
            map((output: RlaDocumentsOutput) => output.documents)
        );
    }

    createAddressDropdownList(contact: InvoiceAddressDetailDto, employments: RlaBusinessDto[], onlyPrivateAddressAllowed: boolean): SelectItem[] {

        let addresses: SelectItem[] = [];
        addresses.push({ value: AppConsts.codes.address.private, label: this.l('Address-Private') + ': ' + this.getPrivateAddressDescription(contact) });

        if (!onlyPrivateAddressAllowed) {
            employments?.forEach((employment) => {
                // Use employment id as item value
                addresses.push({ value: employment.id, label: this.l('EmploymentSingular') + ': ' + this.getEmploymentAddressDescription(employment) });
            });

            addresses.push({ value: AppConsts.codes.address.invoice, label: this.l(AppConsts.codes.address.invoice) });
        }

        return addresses;
    }

    private getPrivateAddressDescription(contact: InvoiceAddressDetailDto) {
        if (!contact) {
            return '';
        }

        let str = contact.street ?? '';

        if (contact.streetNr?.length > 0) {
            str =  str.length > 0 ? str.concat(' ', contact.streetNr) : str.concat(contact.streetNr);
        }

        if (contact.postCode?.length > 0) {
            str = str.length > 0 ? str.concat(', ', contact.postCode) : str.concat(contact.postCode);
        }

        if (contact.city?.length > 0) {
            str = str.length > 0 ? str.concat(' ', contact.city) : str.concat(contact.city);
        }

        return str;
    }

    private getEmploymentAddressDescription(employment: RlaBusinessDto) {
        if (employment == null) {
            return '';
        }

        let str = employment.name ?? '';

        if (employment.street?.length > 0) {
            str =  str.length > 0 ? str.concat(', ', employment.street) : str.concat(employment.street);
        }

        if (employment.streetNr?.length > 0) {
            str =  str.length > 0 ? str.concat(' ', employment.streetNr) : str.concat(employment.streetNr);
        }

        if (employment.postCode?.length > 0) {
            str = str.length > 0 ? str.concat(', ', employment.postCode) : str.concat(employment.postCode);
        }

        if (employment.city?.length > 0) {
            str = str.length > 0 ? str.concat(' ', employment.city) : str.concat(employment.city);
        }

        return str;
    }

    checkDocuments(): Observable<RlaStatus[]> {
        return this.rlaService.validateThatAllMandatoryDocumentsHaveBeenUploaded(this.caseId);
    }

    static getUrlForStep(step: RlaStatus): string {
        switch (step) {
            case RlaStatus.ApplicantPerson:
                return 'applicant-person';
            case RlaStatus.ApplicantContact:
                return 'applicant-contact';
            case RlaStatus.Person:
                return 'person';
            case RlaStatus.Business:
                return 'business';
            case RlaStatus.Replacement:
                return 'replacement';
            case RlaStatus.InvoiceAddress:
                return 'invoice-address';
            case RlaStatus.AcademicTitle:
                return 'academic-title';
            case RlaStatus.MedicalFieldDirector:
                return 'medical-field-director';
            case RlaStatus.Restriction:
                return 'restriction';
            case RlaStatus.Employment:
                return 'employment';
            case RlaStatus.Release:
                return 'release';
            case RlaStatus.FinishReview:
                return 'finish-review';
            default:
                console.error('Invalid status for getUrlForStep');
                return null;
        }
    }
}
